// Adopted heavily from '@angular/service-worker' source code:
// https://github.com/angular/angular/blob/7cabaa0ae745dfbbad664b71da70eec89c17c744/packages/service-worker/src/module.ts
import { Inject, Injectable, Optional } from '@angular/core';

import { OfficeHomePageErrorHandler } from '../error-handler/error-handler.service';
import { ErrorId } from '../error-handler/error-id';
import { WorkloadQueryParameter } from '../shared/constants/workload-query-parameter.enum';
import { WorkloadQueryParameterToken } from '../shared/constants/workload-query-parameter.injection-token';
import { DocumentService } from '../shared/services/document.service';
import { OfficeHomeApiConfigService } from '../shared/services/officehome-api-config.service';
import { WindowService } from '../shared/services/window.service';
import { ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { isFirefoxSecurityError } from './is-firefox-security-error.utility';
import { ServiceWorkerRegistrationHandler, SERVICE_WORKER_REGISTRATION_HANDLERS } from './registration-handlers/service-worker-registration-handlers.injection-token';
import { SERVICE_WORKER_API_AVAILABLE } from './service-worker-api-available.injection-token';
import { ServiceWorkerConfigService } from './service-worker-config.service';
import { SERVICE_WORKER_ENABLED } from './service-worker-enabled.injection-token';
import { SERVICE_WORKER_GET_REGISTRATION_BY_SCOPE } from './service-worker-get-registration-by-scope.injection-token';
import { ServiceWorkerState, ServiceWorkerStateService } from './service-worker-state.service';

// This will run after 'App_Bootstrap_Completed'
@Injectable({
    providedIn: 'root'
})
export class ServiceWorkerInitializer {
    protected serviceWorkerContainer: ServiceWorkerContainer;

    constructor(
        private readonly serviceWorkerConfigService: ServiceWorkerConfigService,
        @Inject(SERVICE_WORKER_ENABLED) private readonly serviceWorkerEnabled: boolean,
        @Inject(SERVICE_WORKER_API_AVAILABLE) private readonly hasServiceWorkerApi,
        protected readonly windowService: WindowService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        @Inject(WorkloadQueryParameterToken) private readonly workloadQueryParameterValue: WorkloadQueryParameter,
        private readonly officeHomeApiConfig: OfficeHomeApiConfigService,
        private readonly serviceWorkerStateService: ServiceWorkerStateService,
        protected readonly documentService: DocumentService,
        @Optional() @Inject(SERVICE_WORKER_REGISTRATION_HANDLERS) private readonly registrationHandlers?: Array<ServiceWorkerRegistrationHandler>,
        @Optional() @Inject(SERVICE_WORKER_GET_REGISTRATION_BY_SCOPE) private readonly getRegistrationByScope?: boolean
    ) {
    }

    // Below code is copied in home.ts for unauth page. Updates should be carried over.
    public async initialize(): Promise<void> {
        if (!this.hasServiceWorkerApi) {
            return;
        }

        this.serviceWorkerContainer = this.windowService.nativeWindow.navigator.serviceWorker;

        try {
            let registration: ServiceWorkerRegistration;

            if (this.serviceWorkerStateService.currentState === ServiceWorkerState.Controlled) {
                // When serviceWorker.getRegistration() is called without explicitly specifying the clientUrl/scope, it takes the value from
                // document.baseURI. Having a <base href='' /> tag changes this value. If the current service worker has a scope that is not
                // set to root, calling getRegistration() will not return anything
                const clientUrl = this.getRegistrationByScope ? this.serviceWorkerConfigService.config.scope : undefined;
                registration = await this.serviceWorkerContainer.getRegistration(clientUrl);
            }

            if (this.serviceWorkerEnabled) {
                const { scriptUrl, scope } = this.serviceWorkerConfigService.config;

                const searchParams = new URLSearchParams();
                searchParams.append('cdnDomain', this.startupConfigService.config.cdnDomain);
                searchParams.append(this.officeHomeApiConfig.config.workloadQueryParameter, this.workloadQueryParameterValue);

                const qsp = new URLSearchParams(this.windowService.nativeWindow.location.search);

                // Register locale parameter for antp
                if (this.workloadQueryParameterValue === WorkloadQueryParameter.Antp) {
                    const locale = qsp.get('locale');
                    const flight = qsp.get('flight');
                    if (locale) {
                        searchParams.append('locale', locale);
                    }
                    if (flight) {
                        searchParams.append('flight', flight);
                    }
                }

                const swFlights = this.getFlightsForRegistrationUrl();
                for (const flight of swFlights) {
                    searchParams.append('serviceWorkerFlight', flight);
                }

                if (this.startupConfigService.config.automatedTestRunning) {
                    const envType = qsp.get('envType');
                    searchParams.append('envType', envType);
                }

                const registrationScriptUrl = `${scriptUrl}?${searchParams.toString()}`;

                // If there was an old registration, we need to see if the URL or scope changed
                // On Edge, this is required because `register(...)` will never resolve if there's already a registration.
                // See MSEdge Bug: https://microsoft.visualstudio.com/DefaultCollection/OS/_workitems/edit/18100326
                if (registration) {
                    const origin = this.windowService.nativeWindow.location.origin;

                    const scriptUrlChanged = registration.active.scriptURL.toLowerCase() !== (origin + registrationScriptUrl).toLowerCase();
                    const scopeChanged = registration.scope.toLowerCase() !== (origin + scope).toLowerCase();

                    // If the URL has changed, or the scope has changed, it will require a call to `register()`
                    if (scriptUrlChanged || scopeChanged) {
                        registration = await this.registerServiceWorker(registrationScriptUrl, scope);
                    }
                } else {
                    registration = await this.registerServiceWorker(registrationScriptUrl, scope);
                }
            }

            if (registration && this.registrationHandlers) {
                await Promise.all(this.registrationHandlers.map(handler => handler.handleRegistration(registration)));
            }
        } catch (ex) {
            if (isFirefoxSecurityError(ex)) {
                return;
            }
            this.globalErrorHandler.handleError(ex, ErrorId.qKgQM);
        }
    }

    protected registerServiceWorker(scriptUrl: string, scope: string): Promise<ServiceWorkerRegistration> {
        return new Promise(resolve => {
            const registerSW = () => {
                resolve(this.serviceWorkerContainer.register(scriptUrl, { scope }));
                this.windowService.nativeWindow.removeEventListener('load', registerSW);
            };
            // Register the worker if the load event already happened
            if (this.documentService.nativeDocument.readyState === 'complete') {
                registerSW();
            } else {
                this.windowService.nativeWindow.addEventListener('load', registerSW);
            }
        });
    }

    private getFlightsForRegistrationUrl(): ReadonlyArray<string> {
        const assignedFlightsString = this.startupConfigService.config.sharedClientStartupContext.flights;
        const { serviceWorkerAllowedFlights, pilotFlights } = this.serviceWorkerConfigService.config;

        const assignedFlights = new Set([
            ...assignedFlightsString.split(','),
            ...pilotFlights
        ]);

        return serviceWorkerAllowedFlights.filter(flight => assignedFlights.has(flight));
    }
}
